import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useStore } from "../store";
import { Gauge } from "../components/Gauge";
import { SlaBar } from "../components/SlaBar";
import { severityColor, statusStyle } from "../data/mockData";
import { useIncidents } from "../api/sitroom";
import { RoleGuard } from "../components/RoleGuard";
import { ObserverBanner } from "../components/ObserverBanner";
import { BreezeStrip } from "../components/Breeze";
import { NextUpWidget } from "../components/NextUpWidget";
import { useAuth } from "../context/AuthContext";
import { useT } from "../lib/i18n";
import { TranslatableText } from "../components/TranslatableText";

const ViewHeader = ({ title, subtitle, actions }) => (
  <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
    <div>
      <h1
        style={{
          fontFamily: "'Syne', system-ui, sans-serif",
          fontWeight: 800,
          fontSize: "clamp(26px, 3.4vw, 34px)",
          color: "#f0f2f8",
          letterSpacing: "-0.5px",
          lineHeight: 1.05,
        }}
        data-testid="view-title"
      >
        {title}
      </h1>
      <div
        className="mt-1.5 font-dm-mono"
        style={{
          fontSize: 11,
          color: "#c5cad8",
          letterSpacing: "0.4px",
        }}
        data-testid="view-subtitle"
      >
        {subtitle}
      </div>
    </div>
    {actions && <div className="flex gap-2 flex-shrink-0">{actions}</div>}
  </div>
);

const KPICard = ({ label, value, valueColor, delta, deltaColor }) => (
  <div
    className="rounded-md p-4"
    style={{
      background: "#111420",
      border: "1px solid rgba(255,255,255,0.12)",
    }}
    data-testid={`kpi-${label.toLowerCase().replace(/\s/g, "-")}`}
  >
    <div
      style={{
        fontFamily: "DM Mono, monospace",
        fontSize: 9,
        letterSpacing: "1.4px",
        color: "#9198b0",
        textTransform: "uppercase",
      }}
    >
      {label}
    </div>
    <div
      className="mt-3"
      style={{
        fontFamily: "DM Mono, monospace",
        fontSize: 36,
        fontWeight: 500,
        color: valueColor,
        lineHeight: 1,
      }}
    >
      {value}
    </div>
    <div
      className="mt-2"
      style={{
        fontFamily: "DM Mono, monospace",
        fontSize: 10,
        color: deltaColor,
      }}
    >
      {delta}
    </div>
  </div>
);

const IncidentRow = ({ inc, onClick }) => {
  const { t } = useT();
  const sev = severityColor(inc.severity);
  const stStyle = statusStyle(inc.status);
  const slaPct = Number.isFinite(inc.sla) ? inc.sla : 0;
  const slaColor = slaPct > 100 ? "#E24B4A" : slaPct > 75 ? "#f39c12" : "#185FA5";
  const slaText = slaPct > 100 ? "BREACH" : `${slaPct}%`;
  return (
    <div
      className="row-hover cursor-pointer"
      style={{
        background: "#0d0f18",
        border: "1px solid rgba(255,255,255,0.06)",
        borderRadius: 6,
        marginBottom: 6,
        overflow: "hidden",
      }}
      onClick={onClick}
      data-testid={`incident-row-${inc.id}`}
    >
      <div
        className="grid grid-cols-[5px_1fr_auto] sm:grid-cols-[5px_110px_1fr_auto_auto_140px_70px] items-center gap-3 px-3 sm:px-4 py-3"
      >
      <div style={{ width: 5, height: 38, background: sev, borderRadius: 2, alignSelf: "stretch" }} />
      <div className="hidden sm:block" style={{ fontFamily: "DM Mono, monospace", fontSize: 10, color: "#9198b0" }}>
        {inc.id}
      </div>
      <div className="min-w-0">
        <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 13, color: "#f0f2f8" }}>
          <TranslatableText text={inc.title} testId={`incident-title-${inc.id}`} />
        </div>
        <div
          className="truncate"
          style={{ fontFamily: "DM Mono, monospace", fontSize: 10, color: "#c5cad8", marginTop: 2 }}
        >
          {inc.parties}
        </div>
      </div>
      <span
        className="hidden sm:inline-block"
        style={{
          background: `${sev}22`,
          border: `1px solid ${sev}80`,
          color: sev,
          fontFamily: "DM Mono, monospace",
          fontSize: 9,
          padding: "2px 7px",
          borderRadius: 3,
          letterSpacing: "1px",
          fontWeight: 500,
        }}
      >
        {inc.severity.toUpperCase()}
      </span>
      <span
        className="hidden sm:inline-block"
        style={{
          background: stStyle.bg,
          border: `1px solid ${stStyle.border}`,
          color: stStyle.color,
          fontFamily: "DM Mono, monospace",
          fontSize: 9,
          padding: "2px 7px",
          borderRadius: 3,
          letterSpacing: "1px",
          fontWeight: 500,
        }}
      >
        {t(`status.${inc.status}`)}
      </span>
      <div className="hidden sm:flex items-center gap-2">
        <div className="relative rounded-full overflow-hidden" style={{ width: 80, height: 5, background: "#181c2a" }}>
          <div
            className="absolute top-0 bottom-0 left-0"
            style={{
              width: `${Math.min(slaPct, 100)}%`,
              background: slaColor,
            }}
          />
        </div>
        <span style={{ fontFamily: "DM Mono, monospace", fontSize: 10, color: slaColor, minWidth: 50 }}>
          {slaText}
        </span>
      </div>
      <div className="hidden sm:block" style={{ fontFamily: "DM Mono, monospace", fontSize: 10, color: "#c5cad8" }}>
        {inc.age}
      </div>
      </div>
      <SlaBar createdAt={inc.createdAt} severity={inc.severity} status={inc.status} height={2} />
    </div>
  );
};

export default function Dashboard() {
  const openModal = useStore((s) => s.openModal);
  const openPanel = useStore((s) => s.openPanel);
  const [now, setNow] = useState(new Date());
  const { data: incidents = [] } = useIncidents();
  const { user } = useAuth();
  const { t } = useT();
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const clock = now.toLocaleTimeString("en-GB", { hour12: false });

  const s1 = incidents.filter((i) => i.severity === "s1");
  // Iter34 Breeze SITREP summary: open S1/S2 · SLA breaches · escalated in last 2h
  const s12 = incidents.filter((i) => i.severity === "s1" || i.severity === "s2");
  const slaBreaches = incidents.filter((i) => Number.isFinite(i.sla) && i.sla > 100).length;
  const last2h = Date.now() - 2 * 3600 * 1000;
  const escalated2h = incidents.filter((i) => {
    const ts = new Date(i.updatedAt || 0).getTime();
    const status = (i.status || "").toLowerCase();
    return status === "escalated" && Number.isFinite(ts) && ts >= last2h;
  }).length;
  const breezeSummary =
    `${s12.length} open S1/S2 · ${slaBreaches} SLA breach${slaBreaches === 1 ? "" : "es"} · ${escalated2h} escalated in last 2h`;
  // Keep `user` referenced (used elsewhere; placeholder for future per-user
  // filters on the board).
  void user;

  return (
    <div data-testid="dashboard-view">
      <ObserverBanner />
      <ViewHeader
        title={t("dashboard.title")}
        subtitle={`${t("dashboard.subtitle")} · ${clock}`}
        actions={
          <>
            <RoleGuard minRole="responder">
              <Link
                to="/incidents/new/chat"
                className="flex items-center gap-1.5 px-3 py-2 rounded-md transition-all"
                style={{
                  background: "rgba(155,89,182,0.15)",
                  color: "#c89adb",
                  border: "1px solid rgba(155,89,182,0.55)",
                  fontFamily: "Syne, sans-serif",
                  fontWeight: 600,
                  fontSize: 12,
                  letterSpacing: "0.4px",
                  textDecoration: "none",
                }}
                data-testid="dashboard-create-with-ai-btn"
              >
                {t("buttons.create_with_ai")}
              </Link>
              <button
                onClick={openModal}
                className="px-3 py-2 rounded-md"
                style={{
                  background: "#185FA5",
                  color: "#fff",
                  fontFamily: "Syne, sans-serif",
                  fontWeight: 700,
                  fontSize: 12,
                  letterSpacing: "0.4px",
                }}
                data-testid="new-incident-btn"
              >
                {t("buttons.new_incident")}
              </button>
            </RoleGuard>
          </>
        }
      />

      <div
        className="grid grid-cols-1 lg:grid-cols-2 gap-4"
        style={{ alignItems: "stretch" }}
        data-testid="dashboard-top-row"
      >
        {/* Risk Index card — 50% width, min-height 220px, visually dominant */}
        <div
          className="rounded-md p-4 flex flex-col"
          style={{
            background: "#111420",
            border: "1px solid rgba(255,255,255,0.12)",
            minHeight: 220,
          }}
          data-testid="gauge-card"
        >
          <div
            style={{
              fontFamily: "DM Mono, monospace",
              fontSize: 9,
              letterSpacing: "1.4px",
              color: "#9198b0",
              textTransform: "uppercase",
            }}
          >
            Escalation Risk Index
          </div>
          <div className="grid grid-cols-[220px_1fr] gap-5 mt-2 flex-1 items-center">
            <div className="flex flex-col items-center">
              <Gauge value={78} />
              <div
                className="text-center mt-1"
                style={{
                  fontFamily: "DM Mono, monospace",
                  fontSize: 10,
                  color: "#f3b14a",
                  letterSpacing: "1.2px",
                }}
              >
                HIGH RISK
              </div>
              <div className="grid grid-cols-4 gap-1 mt-3 w-full">
                {[
                  { l: "Low", c: "#27ae60", active: false },
                  { l: "Elev.", c: "#9198b0", active: false },
                  { l: "High", c: "#f39c12", active: true },
                  { l: "Crit.", c: "#E24B4A", active: false },
                ].map((b) => (
                  <div key={b.l} className="text-center">
                    <div
                      style={{
                        height: b.active ? 5 : 3,
                        background: b.c,
                        borderRadius: 2,
                        opacity: b.active ? 1 : 0.5,
                        boxShadow: b.active ? `0 0 6px ${b.c}` : "none",
                      }}
                    />
                    <div
                      className="mt-1"
                      style={{
                        fontFamily: "DM Mono, monospace",
                        fontSize: 8,
                        color: b.active ? b.c : "#9198b0",
                        letterSpacing: "0.4px",
                      }}
                    >
                      {b.l}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {/* 2x2 sub-stats on the right side of the gauge card */}
            <div className="grid grid-cols-2 gap-2 h-full content-center">
              {[
                { v: "3×", c: "#E24B4A", l: "Active S1" },
                { v: "78%", c: "#f3b14a", l: "SLA breach rate" },
                { v: "2×", c: "#f3b14a", l: "Escalated 2h" },
                { v: "2/7", c: "#f3b14a", l: "Lines at risk" },
              ].map((m, i) => (
                <div
                  key={i}
                  className="rounded-md p-2.5"
                  style={{ background: "#0d0f18", border: "1px solid rgba(255,255,255,0.06)" }}
                >
                  <div
                    style={{
                      fontFamily: "DM Mono, monospace",
                      fontSize: 18,
                      color: m.c,
                      fontWeight: 500,
                    }}
                  >
                    {m.v}
                  </div>
                  <div
                    style={{
                      fontFamily: "DM Mono, monospace",
                      fontSize: 9,
                      color: "#9198b0",
                      letterSpacing: "0.6px",
                      textTransform: "uppercase",
                      marginTop: 2,
                    }}
                  >
                    {m.l}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* KPI 2×2 grid — 50% width, equal sizing, same height */}
        <div
          className="grid grid-cols-2 grid-rows-2 gap-3"
          style={{ minHeight: 220 }}
          data-testid="dashboard-kpi-grid"
        >
          <KPICard label="S1 Critical" value="3" valueColor="#E24B4A" delta="↑ 2 since last shift" deltaColor="#ff7e7c" />
          <KPICard label="Total Open" value="14" valueColor="#f0f2f8" delta="↑ 5 in last 24h" deltaColor="#ff7e7c" />
          <KPICard label="SLA Compliance" value="84%" valueColor="#f3b14a" delta="↓ 3pp vs last week" deltaColor="#ff7e7c" />
          <KPICard label="Avg MTTR S1" value="2.4h" valueColor="#4ed084" delta="↓ 0.6h improvement" deltaColor="#4ed084" />
        </div>
      </div>

      {/* Iter55 — "Next up" widget surfaces the top-3 most urgent items from
          the Countdown feed so operators see what's burning the moment they
          log in. Sits between the Risk/KPI row and the Breeze kanban so the
          Breeze remains the centre of gravity but urgency is still glanceable. */}
      <div className="mt-4" data-testid="dashboard-next-up-row">
        <NextUpWidget />
      </div>

      {/* Breeze — situational strip between Risk row and Active S1 list */}
      <BreezeStrip
        incidents={incidents}
        summary={breezeSummary}
      />

      {/* Active S1 Incidents */}
      <div
        className="rounded-md p-4 mt-4"
        style={{
          background: "#111420",
          border: "1px solid rgba(255,255,255,0.12)",
        }}
        data-testid="active-s1-card"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span
              className="inline-block rounded-full pulse-red"
              style={{ width: 9, height: 9, background: "#E24B4A" }}
            />
            <span
              style={{
                fontFamily: "Syne, sans-serif",
                fontWeight: 700,
                fontSize: 14,
                color: "#f0f2f8",
                letterSpacing: "0.3px",
              }}
            >
              Active S1 Incidents
            </span>
          </div>
          <Link
            to="/incidents"
            style={{
              fontFamily: "DM Mono, monospace",
              fontSize: 10,
              color: "#9ec3ec",
              letterSpacing: "0.5px",
            }}
            data-testid="view-all-incidents-link"
          >
            View all →
          </Link>
        </div>
        <div>
          {s1.map((inc) => (
            <IncidentRow key={inc.id} inc={inc} onClick={() => openPanel(inc.id)} />
          ))}
        </div>
      </div>
    </div>
  );
}
